import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import HeadSync from "@/components/HeadSync";

import { ThemeProvider } from "next-themes";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import LanguageLayout from "@/layouts/LanguageLayout";
const Header = lazy(() => import("@/components/Header"));
const Footer = lazy(() => import("@/components/Footer"));
const Index = lazy(() => import("./pages/Index"));

// Lazy-loaded pages to reduce initial bundle
const Privacy = lazy(() => import("./pages/Privacy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Termini = lazy(() => import("./pages/Termini"));
const Contatti = lazy(() => import("./pages/Contatti"));
const ChiSiamo = lazy(() => import("./pages/ChiSiamo"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const GuidaCompleta = lazy(() => import("./pages/GuidaCompleta"));
const ContatoreParole = lazy(() => import("./pages/ContatoreParole"));
const ParafrasatoreOnline = lazy(() => import("./pages/ParafrasatoreOnline"));
const SemplificatoreOnline = lazy(() => import("./pages/SemplificatoreOnline"));
const EssayShortener = lazy(() => import("./pages/EssayShortener"));
const GrammarChecker = lazy(() => import("./pages/GrammarChecker"));
const ParagraphRewriter = lazy(() => import("./pages/ParagraphRewriter"));
const AiWritingToolsGuide = lazy(() => import("./pages/AiWritingToolsGuide"));
const NicheLanding = lazy(() => import("./pages/NicheLanding"));
const LongTailPage = lazy(() => import("./pages/LongTailPage"));
const FreeAiToolsDirectory = lazy(() => import("./pages/FreeAiToolsDirectory"));
const HowAiWorks = lazy(() => import("./pages/HowAiWorks"));
const EditorialStandards = lazy(() => import("./pages/EditorialStandards"));
const EsempioRiassunto = lazy(() => import("./pages/EsempioRiassunto"));
const ValutareRiassunto = lazy(() => import("./pages/ValutareRiassunto"));
const GuideAcademicSummarization = lazy(() => import("./pages/GuideAcademicSummarization"));
const RiassumereTesto = lazy(() => import("./pages/RiassumereTesto"));
const AcademicWritingResources = lazy(() => import("./pages/AcademicWritingResources"));
const AiToolsComparison = lazy(() => import("./pages/AiToolsComparison"));
const AiWritingTrends = lazy(() => import("./pages/AiWritingTrends"));
const SharedSummary = lazy(() => import("./pages/SharedSummary"));
const SummarizeLecture = lazy(() => import("./pages/SummarizeLecture"));
const EnLandingPage = lazy(() => import("./pages/EnLandingPage"));
const FreeAiToolForStudents = lazy(() => import("./pages/FreeAiToolForStudents"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProgrammaticPage = lazy(() => import("./pages/ProgrammaticPage"));
const AiEditor = lazy(() => import("./pages/AiEditor"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <AuthProvider>
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <HeadSync />
          <Suspense fallback={<div className="min-h-screen" />}>

          <Routes>
            {/* Redirect root to locale */}
            <Route path="/" element={<Navigate to="/it/" replace />} />
            
            {/* Share route (no language prefix) */}
            <Route path="/share/:shortId" element={<SharedSummary />} />

            {/* Language-prefixed routes */}
            <Route path="/:lang" element={<LanguageLayout />}>
              <Route element={<LayoutWrapper />}>
                <Route index element={<Index />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="cookie-policy" element={<CookiePolicy />} />
                <Route path="termini" element={<Termini />} />
                <Route path="contatti" element={<Contatti />} />
                <Route path="chi-siamo" element={<ChiSiamo />} />
                <Route path="blog" element={<Blog />} />
                <Route path="blog/:slug" element={<BlogPost />} />
                <Route path="guida-completa-riassunto-online" element={<GuidaCompleta />} />
                <Route path="contatore-parole" element={<ContatoreParole />} />
                <Route path="parafrasatore-online" element={<ParafrasatoreOnline />} />
                <Route path="semplificare-testo-online" element={<SemplificatoreOnline />} />
                {/* Tools */}
                <Route path="essay-shortener" element={<EssayShortener />} />
                <Route path="accorciatore-testi" element={<EssayShortener />} />
                <Route path="grammar-checker" element={<GrammarChecker />} />
                <Route path="correttore-grammaticale" element={<GrammarChecker />} />
                <Route path="paragraph-rewriter" element={<ParagraphRewriter />} />
                <Route path="riscrittore-paragrafi" element={<ParagraphRewriter />} />
                {/* Pillar pages */}
                <Route path="ai-writing-tools-guide" element={<AiWritingToolsGuide />} />
                <Route path="guida-strumenti-scrittura-ia" element={<AiWritingToolsGuide />} />
                {/* Niche landing pages */}
                <Route path="student-text-summarizer" element={<NicheLanding />} />
                <Route path="academic-paper-summarizer" element={<NicheLanding />} />
                <Route path="business-report-summarizer" element={<NicheLanding />} />
                <Route path="study-notes-summarizer" element={<NicheLanding />} />
                <Route path="book-chapter-summarizer" element={<NicheLanding />} />
                {/* Long-tail pages — catch-all slug lookup (must be after all specific routes) */}
                {/* Authority pages */}
                <Route path="free-ai-tools" element={<FreeAiToolsDirectory />} />
                <Route path="strumenti-ia-gratis" element={<FreeAiToolsDirectory />} />
                <Route path="how-our-ai-works" element={<HowAiWorks />} />
                <Route path="come-funziona-la-nostra-ia" element={<HowAiWorks />} />
                <Route path="esempio-riassunto-passo-passo" element={<EsempioRiassunto />} />
                <Route path="summary-example-step-by-step" element={<EsempioRiassunto />} />
                <Route path="valutare-qualita-riassunto-ia" element={<ValutareRiassunto />} />
                <Route path="evaluate-ai-summary-quality" element={<ValutareRiassunto />} />
                <Route path="editorial-standards" element={<EditorialStandards />} />
                <Route path="standard-editoriali" element={<EditorialStandards />} />
                {/* Linkbait & distribution pages */}
                <Route path="guide-to-academic-summarization" element={<GuideAcademicSummarization />} />
                <Route path="guida-riassunto-accademico" element={<GuideAcademicSummarization />} />
                <Route path="academic-writing-resources" element={<AcademicWritingResources />} />
                <Route path="risorse-scrittura-accademica" element={<AcademicWritingResources />} />
                <Route path="ai-tools-comparison" element={<AiToolsComparison />} />
                <Route path="confronto-strumenti-ia" element={<AiToolsComparison />} />
                <Route path="ai-writing-trends-2025" element={<AiWritingTrends />} />
                <Route path="tendenze-scrittura-ia-2025" element={<AiWritingTrends />} />
                {/* Viral pages */}
                {/* High-intent landing pages */}
                <Route path="riassumere-testo-online" element={<RiassumereTesto pageSlug="riassumere-testo-online" />} />
                <Route path="riassumere-pdf" element={<RiassumereTesto pageSlug="riassumere-pdf" />} />

                <Route path="riassunto-automatico" element={<RiassumereTesto pageSlug="riassunto-automatico" />} />
                <Route path="automatic-summary" element={<RiassumereTesto pageSlug="riassunto-automatico" />} />
                <Route path="riassunto-articolo-online" element={<RiassumereTesto pageSlug="riassunto-articolo" />} />
                <Route path="summarize-article" element={<RiassumereTesto pageSlug="riassunto-articolo" />} />
                <Route path="summarize-your-professor-lecture" element={<SummarizeLecture />} />
                <Route path="free-ai-tool-for-students" element={<FreeAiToolForStudents />} />
                {/* EN high-intent landing pages */}
                <Route path="ai-text-summarizer" element={<EnLandingPage pageSlug="ai-text-summarizer" />} />
                <Route path="summarize-text-online" element={<EnLandingPage pageSlug="summarize-text-online" />} />
                <Route path="summarize-pdf" element={<EnLandingPage pageSlug="summarize-pdf" />} />
                <Route path="free-ai-writing-tool" element={<EnLandingPage pageSlug="free-ai-writing-tool" />} />
                {/* Auth & Dashboard */}
                <Route path="auth" element={<Auth />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="ai-editor" element={<AiEditor />} />
                <Route path="editor-ia" element={<AiEditor />} />
                {/* Programmatic SEO pages */}
                <Route path="riassunto-libro/:topic" element={<ProgrammaticPage />} />
                <Route path="riassunto-pdf/:topic" element={<ProgrammaticPage />} />
                <Route path="riassunto-articolo/:topic" element={<ProgrammaticPage />} />
                {/* Catch-all for 120+ long-tail pages */}
                <Route path=":slug" element={<LongTailPage />} />
              </Route>
            </Route>

            {/* Legacy routes redirect to /it/ */}
            <Route path="/blog" element={<Navigate to="/it/blog" replace />} />
            <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
            <Route path="/privacy" element={<Navigate to="/it/privacy" replace />} />
            <Route path="/termini" element={<Navigate to="/it/termini" replace />} />
            <Route path="/contatti" element={<Navigate to="/it/contatti" replace />} />
            <Route path="/chi-siamo" element={<Navigate to="/it/chi-siamo" replace />} />
            <Route path="/guida-completa-riassunto-online" element={<Navigate to="/it/guida-completa-riassunto-online" replace />} />
            <Route path="/contatore-parole" element={<Navigate to="/it/contatore-parole" replace />} />
            <Route path="/parafrasatore-online" element={<Navigate to="/it/parafrasatore-online" replace />} />
            <Route path="/semplificare-testo-online" element={<Navigate to="/it/semplificare-testo-online" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
  </AuthProvider>
);

// Wrapper that adds Header + Footer around Outlet
import { Outlet } from "react-router-dom";
function LayoutWrapper() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1"><Outlet /></div>
      <Footer />
    </div>
  );
}

// Legacy blog redirect
import { useParams } from "react-router-dom";
function LegacyBlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/it/blog/${slug}`} replace />;
}

export default App;
